// Job Request Service
// Handles user job requests - users can post custom work requests instead of applying to existing jobs

import { db } from '../config/firebase';
import { ref, set, push, get } from 'firebase/database';
import { validateJob, normalizeSkillsArray, identifyPrimarySkill, LUZON_LOCATIONS } from '../utils/jobSchema';

const JOB_REQUESTS_REF = 'jobRequests';

// Re-export for backward compatibility
export { LUZON_LOCATIONS };

/**
 * Wage structure by skill level and region
 */
export const WAGE_STRUCTURE = {
  laborer: { multiplier: 1.0, description: 'Laborer (Minimum Wage)' },
  skilled: { multiplier: 1.5, description: 'Skilled Worker (50% above minimum)' },
  specialist: { multiplier: 2.0, description: 'Specialist (100% above minimum)' },
};

/**
 * Post a new job request
 * @param {string} userId - User ID
 * @param {Object} requestData - Job request data
 * @returns {Promise<Object>} - Created request
 */
export const postJobRequest = async (userId, requestData) => {
  try {
    const jobRequestsRef = ref(db, `${JOB_REQUESTS_REF}/${userId}`);
    const newRequestRef = push(jobRequestsRef);

    // Validate location
    const location = requestData.location;
    const locationData = LUZON_LOCATIONS[location];
    if (!locationData) {
      throw new Error('Invalid location selected');
    }

    // Calculate wage based on skill level and location
    const skillLevel = requestData.skillLevel;
    const wageMultiplier = WAGE_STRUCTURE[skillLevel]?.multiplier || 1;
    const dailyWage = Math.round(locationData.minWage * wageMultiplier);

    // Calculate total budget if days are provided
    const estimatedDays = parseInt(requestData.estimatedDays) || 1;
    const estimatedBudget = dailyWage * estimatedDays;

    const request = {
      requestId: newRequestRef.key,
      userId,
      title: requestData.title,
      description: requestData.description,
      skillLevel,
      location,
      status: 'pending',
      estimatedDays,
      dailyWage,
      estimatedBudget,
      images: requestData.images || [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      adminNotes: '',
    };

    await set(newRequestRef, request);

    console.log(
      '%c[SUCCESS] Job request posted',
      'background: #4CAF50; color: white; padding: 5px 10px; border-radius: 3px; font-weight: bold;',
      { userId, title: requestData.title }
    );

    return request;
  } catch (error) {
    console.error('Error posting job request:', error);
    throw error;
  }
};

/**
 * Get all job requests by user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - User's job requests
 */
export const getUserJobRequests = async (userId) => {
  try {
    const requestsRef = ref(db, `${JOB_REQUESTS_REF}/${userId}`);
    const snapshot = await get(requestsRef);

    if (snapshot.exists()) {
      const requestsData = snapshot.val();
      const requests = Object.entries(requestsData).map(([key, value]) => ({
        id: key,
        ...value,
      }));
      return requests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    return [];
  } catch (error) {
    console.error('Error fetching user job requests:', error);
    return [];
  }
};

/**
 * Get all job requests (for admin)
 * @returns {Promise<Array>} - All job requests
 */
export const getAllJobRequests = async () => {
  try {
    const requestsRef = ref(db, JOB_REQUESTS_REF);
    const snapshot = await get(requestsRef);

    if (snapshot.exists()) {
      const allRequests = [];
      const usersData = snapshot.val();

      Object.entries(usersData).forEach(([userId, userRequests]) => {
        Object.entries(userRequests).forEach(([requestId, request]) => {
          allRequests.push({
            id: requestId,
            ...request,
            userId,
          });
        });
      });

      return allRequests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }
    return [];
  } catch (error) {
    console.error('Error fetching all job requests:', error);
    return [];
  }
};

/**
 * Update job request status
 * @param {string} userId - User ID
 * @param {string} requestId - Request ID
 * @param {string} newStatus - New status (pending, under_review, accepted, rejected)
 * @param {string} adminNotes - Admin notes/feedback
 * @returns {Promise<Object>} - Updated request
 */
export const updateJobRequestStatus = async (userId, requestId, newStatus, adminNotes = '') => {
  try {
    const requestRef = ref(db, `${JOB_REQUESTS_REF}/${userId}/${requestId}`);
    const snapshot = await get(requestRef);

    if (snapshot.exists()) {
      const requestData = snapshot.val();
      const updates = {
        ...requestData,
        status: newStatus,
        adminNotes,
        updatedAt: new Date().toISOString(),
      };

      await set(requestRef, updates);

      console.log(
        '%c[SUCCESS] Job request status updated',
        'background: #4CAF50; color: white; padding: 5px 10px; border-radius: 3px; font-weight: bold;',
        { userId, requestId, newStatus }
      );

      return updates;
    }
    throw new Error('Job request not found');
  } catch (error) {
    console.error('Error updating job request status:', error);
    throw error;
  }
};

/**
 * Delete job request (only pending requests can be deleted)
 * @param {string} userId - User ID
 * @param {string} requestId - Request ID
 * @returns {Promise<void>}
 */
export const deleteJobRequest = async (userId, requestId) => {
  try {
    const requestRef = ref(db, `${JOB_REQUESTS_REF}/${userId}/${requestId}`);
    const snapshot = await get(requestRef);

    if (snapshot.exists()) {
      const request = snapshot.val();
      if (request.status !== 'pending') {
        throw new Error('Only pending requests can be deleted');
      }

      await set(requestRef, null);
      console.log('Job request deleted:', requestId);
    }
  } catch (error) {
    console.error('Error deleting job request:', error);
    throw error;
  }
};

/**
 * Get geolocation-based wage estimate
 * @param {string} location - Location name
 * @param {string} skillLevel - Skill level
 * @param {number} days - Estimated days
 * @returns {Object} - Wage estimate
 */
export const getWageEstimate = (location, skillLevel, days = 1) => {
  const locationData = LUZON_LOCATIONS[location];
  if (!locationData) {
    return null;
  }

  const wageMultiplier = WAGE_STRUCTURE[skillLevel]?.multiplier || 1;
  const dailyWage = Math.round(locationData.minWage * wageMultiplier);
  const totalBudget = dailyWage * days;

  return {
    location,
    minWage: locationData.minWage,
    skillLevel,
    skillDescription: WAGE_STRUCTURE[skillLevel].description,
    dailyWage,
    estimatedDays: days,
    totalBudget,
  };
};

/**
 * Convert accepted job request to job posting
 * Uses unified schema for consistency with admin-created jobs
 * @param {Object} jobRequest - Job request data
 * @param {string} userId - User who posted the request
 * @returns {Object} - Job object ready to be posted
 */
export const convertJobRequestToJob = (jobRequest, userId = '') => {
  const locationData = LUZON_LOCATIONS[jobRequest.location];
  if (!locationData) {
    throw new Error('Invalid location in job request');
  }

  // Calculate salary based on skill level and location
  const wageMultiplier = WAGE_STRUCTURE[jobRequest.skillLevel]?.multiplier || 1;
  const dailyWage = Math.round(locationData.minWage * wageMultiplier);
  const monthlyEstimate = dailyWage * 20; // Assuming ~20 working days per month

  // Normalize skills
  const skills = normalizeSkillsArray(jobRequest.skills);

  // Identify primary skill from title and description
  const primary_skill = identifyPrimarySkill(
    jobRequest.title || '',
    jobRequest.description || '',
    skills
  );

  // Unified job structure
  const job = {
    // Identifiers
    sourceType: 'request',
    sourceRequestId: jobRequest.requestId || jobRequest.id,
    postedBy: userId,

    // Basic Info
    title: jobRequest.title || 'Custom Work Request',
    description: jobRequest.description || 'Custom work request',
    companyName: 'Alacritas AI - Job Request',

    // Skill Information (CRITICAL FOR MATCHING)
    primary_skill: primary_skill,
    skills: skills,
    skillLevel: jobRequest.skillLevel,
    experience: 'Varies',

    // Location Info (with coordinates for better matching)
    location: jobRequest.location,
    coordinates: {
      latitude: locationData.lat,
      longitude: locationData.lng
    },
    latitude: locationData.lat,
    longitude: locationData.lng,

    // Job Details
    type: 'Contract',
    workplace: 'On-site',
    hours: 'Negotiable',
    vacancy: '1',
    rate: `PHP ${dailyWage}/day (~PHP ${monthlyEstimate}/month)`,

    // Employment Details
    education: WAGE_STRUCTURE[jobRequest.skillLevel]?.description || 'Experience required',
    benefits: [],

    // Meta
    status: 'approved',
    icon: '💼',
    image: '/images/sourced/job-default.png',

    // Timestamps
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };

  // Validate and normalize the job
  return validateJob(job);
};
